import React from "react";
import { cn } from "@/lib/utils";
import { TRADE_POLICY_BUCKET_METRIC_DEFINITIONS } from "../../../constants/tradePolicyBucketMetricDefinitions";
import type { TradePolicyCatalogField } from "../../../types/tradePolicy";
import { formatSessionDisplayName } from "../../../utils/sessionDisplayName";
import { catalogPercentHumanDisplayScale } from "./policyRuleNumericValue";
import { POLICY_TREE_FIELD_LABEL_CLASS, POLICY_TREE_SUBTLE_TEXT_CLASS } from "./policyTreeTheme";

type BucketMetricDef = {
  metric?: string;
  buckets?: Record<string, { rule?: string; meaning?: string }>;
};

function safeBucketMetricDef(metricKey: string): BucketMetricDef | null {
  const root = TRADE_POLICY_BUCKET_METRIC_DEFINITIONS as Record<string, unknown>;
  const raw = root[metricKey];
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const bucketsRaw = o.buckets;
  const buckets =
    bucketsRaw && typeof bucketsRaw === "object" && !Array.isArray(bucketsRaw)
      ? (bucketsRaw as Record<string, unknown>)
      : null;
  const outBuckets: Record<string, { rule?: string; meaning?: string }> = {};
  if (buckets) {
    for (const [k, v] of Object.entries(buckets)) {
      if (v && typeof v === "object" && !Array.isArray(v)) {
        const vv = v as Record<string, unknown>;
        outBuckets[k] = {
          rule: typeof vv.rule === "string" ? vv.rule : vv.rule != null ? String(vv.rule) : undefined,
          meaning: typeof vv.meaning === "string" ? vv.meaning : vv.meaning != null ? String(vv.meaning) : undefined,
        };
      }
    }
  }
  return {
    metric: typeof o.metric === "string" ? o.metric : o.metric != null ? String(o.metric) : undefined,
    buckets: Object.keys(outBuckets).length ? outBuckets : undefined,
  };
}

const CATALOG_PATH_TO_METRIC_KEY: Record<string, string> = {
  "structure.adx": "adx",
  "volatility.bollinger_width_pct": "squeeze",
  "volatility.atr_pct_price": "atr_regime",
  "regime.market_condition": "market_condition",
  "trade.trade_type": "trade_direction",
};

function metricKeyForCatalogPath(path: string): string | null {
  if (CATALOG_PATH_TO_METRIC_KEY[path]) return CATALOG_PATH_TO_METRIC_KEY[path];
  const pl = path.toLowerCase();
  if (pl.includes("session_id") || pl.includes("session_time")) return "session";
  if (pl.includes("market_condition") || pl.includes("candle_regime") || pl.includes("market_regime")) {
    return "market_condition";
  }
  if (pl.includes("candle_direction_short") || pl.includes("direction_short")) return "candle_direction_short";
  if (pl.includes("candle_direction")) return "candle_direction";
  if (pl.includes("trade_type") || pl.includes("trade_direction")) return "trade_direction";
  return null;
}

function metricKeyForCatalogField(field: TradePolicyCatalogField): string | null {
  const fromPath = metricKeyForCatalogPath(field.path);
  if (fromPath) return fromPath;

  const pl = field.path.toLowerCase();
  const labelHay = `${field.path} ${field.label}`.toLowerCase();
  const root = TRADE_POLICY_BUCKET_METRIC_DEFINITIONS as Record<string, unknown>;
  let bestKey: string | null = null;
  let bestScore = 0;

  for (const mk of Object.keys(root)) {
    let score = 0;
    if (labelHay.includes(mk)) score += 24;
    if (pl.endsWith(`.${mk}`) || pl.endsWith(`_${mk}`) || pl.includes(`.${mk}.`) || pl.includes(`_${mk}_`)) {
      score += 28;
    }
    if (pl.includes(`${mk}_bucket`)) score += 22;

    if (field.value_kind === "enum") {
      const def = safeBucketMetricDef(mk);
      const bucketKeys = def?.buckets ? Object.keys(def.buckets) : [];
      const ev = new Set((field.enum_values ?? []).map((e) => e.toLowerCase().replace(/\s+/g, "_")));
      let overlap = 0;
      for (const bk of bucketKeys) {
        if (ev.has(bk.toLowerCase())) overlap += 1;
      }
      if (overlap >= 2) score += overlap * 8;
      else if (overlap === 1 && score >= 20) score += 4;
    }

    if (score > bestScore) {
      bestScore = score;
      bestKey = mk;
    }
  }
  return bestScore >= 20 ? bestKey : null;
}

export function numericGlossaryHelperForPath(path: string): string | null {
  const metricKey = metricKeyForCatalogPath(path);
  if (!metricKey) return null;
  const def = safeBucketMetricDef(metricKey);
  const buckets = def?.buckets;
  if (!buckets) return null;
  const pairs = Object.entries(buckets)
    .filter(([key]) => isUserFacingBucketKey(key))
    .map(([k, v]) => ({ k, rule: v.rule }))
    .filter((x) => Boolean(x.rule));
  if (pairs.length === 0) return null;
  const lines = pairs.map((p) => `${p.k}: ${p.rule}`);
  return `Glossary thresholds:\n${lines.join("\n")}`;
}

export type PolicyRuleFieldExplanation = {
  summary: string;
};

const INTERNAL_BUCKET_OPTION_KEYS = new Set([
  "unknown",
  "unset",
  "n/a",
  "na",
  "none",
  "missing",
  "other",
]);

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function isUserFacingBucketKey(key: string): boolean {
  const normalized = key.trim().toLowerCase().replace(/\s+/g, "_");
  return normalized.length > 0 && !INTERNAL_BUCKET_OPTION_KEYS.has(normalized);
}

function userFacingEnumValues(values: string[]): string[] {
  return values.filter(isUserFacingBucketKey);
}

const USER_FACING_METRIC_INTROS: Record<string, string> = {
  session:
    "The trading session active when you entered the trade, such as Tokyo, London, Sydney, or New York, including overlap windows between sessions.",
  market_condition:
    "The broad market regime at entry (classifier v3): uptrend, downtrend, range, choppy, or unknown.",
  regime:
    "The broad market regime at entry (classifier v3): uptrend, downtrend, range, choppy, or unknown.",
  candle_direction:
    "Whether price was moving up, down, or flat over the longer lookback window at entry.",
  candle_direction_short:
    "Whether price was moving up, down, or flat over the shorter lookback window at entry.",
  trade_direction: "Whether you took a long or short position.",
  direction: "Whether you took a long or short position.",
};

const POLICY_ENUM_LABEL_OVERRIDES: Record<string, string> = {
  not_aligned: "Not aligned",
  strongly_aligned: "Strongly aligned",
  partially_aligned: "Partially aligned",
  very_strong: "Very strong",
  mid_range: "Mid range",
  at_support: "At support",
  at_resistance: "At resistance",
  tight_squeeze: "Tight squeeze",
  very_tight: "Very tight",
  new_york: "New York",
};

function isSessionField(field: TradePolicyCatalogField): boolean {
  const hay = `${field.path} ${field.label}`.toLowerCase();
  return hay.includes("session");
}

function formatPolicyEnumDisplayValue(value: string, field: TradePolicyCatalogField): string {
  const token = value.trim();
  if (!token) return token;

  const normalized = token.toLowerCase().replace(/\s+/g, "_");
  if (POLICY_ENUM_LABEL_OVERRIDES[normalized]) return POLICY_ENUM_LABEL_OVERRIDES[normalized];
  if (isSessionField(field)) return formatSessionDisplayName(token);

  if (normalized.includes("_")) {
    return normalized
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }

  return token.charAt(0).toUpperCase() + token.slice(1);
}

function formatUserFacingEnumList(values: string[], field: TradePolicyCatalogField): string {
  return userFacingEnumValues(values)
    .map((value) => formatPolicyEnumDisplayValue(value, field))
    .join(", ");
}

function humanizeTechnicalText(text: string): string {
  return text
    .replace(/\bcatalog path:?\s*[\w.]+/gi, "")
    .replace(/Full cohort slices use[^.]*\.\s*/gi, "")
    .replace(/canonical ids on trades:[^.]*\.\s*/gi, "")
    .replace(/Keys:\s*[^.]*\.\s*/gi, "")
    .replace(/\bstats\.[\w.]+/gi, "")
    .replace(/\bmeta\.[\w.]+/gi, "")
    .replace(/\bsession_at_entry\.[\w.]+/gi, "")
    .replace(/\bMarginal cohort[^.]*\.\s*/gi, "")
    .replace(/\bAlias of [^.]*\.\s*/gi, "")
    .replace(/\bCombined slice at entry:\s*/gi, "")
    .replace(/\bCombined 3-way slice at entry:\s*/gi, "")
    .replace(/\s*\(percent of price\)/gi, "")
    .replace(/\s*\(not a 0–1 score\)/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function simplifyMetricDescription(text: string): string {
  let cleaned = humanizeTechnicalText(text);
  const colonParts = cleaned.split(/:\s+/);
  if (colonParts.length >= 2 && /[÷×]/.test(colonParts[0])) {
    cleaned = colonParts.slice(1).join(": ").trim();
  }
  const sentences = cleaned.split(/(?<=[.!?])\s+/).filter(Boolean);
  if (sentences.length > 2) {
    return sentences.slice(0, 2).join(" ");
  }
  return cleaned;
}

function looksTechnicalBackendCopy(text: string): boolean {
  return /\bcatalog\b|path:\s*[\w.]+|policy engine|fraction of 100|bucket rules|glossary threshold|stats\.|meta\.|session_contract|session_at_entry|condition_analysis|regime_breakdown|canonical ids/i.test(
    text
  );
}

function metricIntroFromMergedDef(merged: unknown): string {
  if (typeof merged === "string") {
    const cleaned = humanizeTechnicalText(merged.trim());
    return cleaned && !looksTechnicalBackendCopy(cleaned) ? cleaned : "";
  }
  if (!isPlainRecord(merged)) return "";
  for (const key of ["metric", "definition", "description", "meaning"] as const) {
    const value = merged[key];
    if (typeof value === "string" && value.trim()) {
      const cleaned = humanizeTechnicalText(value.trim());
      if (cleaned && !looksTechnicalBackendCopy(cleaned)) return cleaned;
    }
  }
  return "";
}

function isSimpleThreshold(rule: string): boolean {
  if (rule.length > 48) return false;
  return !/distance_|position_ratio|alignment_score|zone strength|weighted score|room </i.test(rule);
}

function simplifyThresholdRule(rule: string): string {
  let simplified = rule
    .replace(/^ADX\s*/i, "")
    .replace(/width_pct/gi, "band width")
    .replace(/ATR%/gi, "volatility")
    .trim();

  const gteOnly = simplified.match(/^≥\s*([\d.%]+)$/);
  if (gteOnly) return `${gteOnly[1]} or above`;

  const ltOnly = simplified.match(/^<\s*([\d.%]+)$/);
  if (ltOnly) return `below ${ltOnly[1]}`;

  const range = simplified.match(/^([\d.%]+)\s*≤\s*[^<]+\s*<\s*([\d.%]+)$/);
  if (range) return `${range[1]}–${range[2]}`;

  return simplified;
}

function formatBucketRuleForDisplay(rule: string, field: TradePolicyCatalogField): string {
  if (catalogPercentHumanDisplayScale(field) <= 1) return rule;
  return rule
    .replace(/\b0\.3\b/g, "30%")
    .replace(/\b0\.8\b/g, "80%")
    .replace(/\s*\(percent of price\)/gi, "")
    .replace(/\s*\(not a 0–1 score\)/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function buildUserFacingBucketOptions(
  field: TradePolicyCatalogField,
  buckets: Record<string, { rule?: string; meaning?: string }>
): string {
  const lines = Object.entries(buckets)
    .filter(([key]) => isUserFacingBucketKey(key))
    .map(([key, value]) => {
      const meaning = value.meaning?.trim();
      if (!meaning) return null;
      return `${formatPolicyEnumDisplayValue(key, field)} (${meaning.charAt(0).toLowerCase() + meaning.slice(1)})`;
    })
    .filter((line): line is string => Boolean(line));

  if (lines.length === 0) return "";
  return `Options include ${lines.join(", ")}.`;
}

function buildNumericThresholdGuidance(
  field: TradePolicyCatalogField,
  buckets: Record<string, { rule?: string; meaning?: string }>
): string {
  const guides = Object.entries(buckets)
    .filter(([key]) => isUserFacingBucketKey(key))
    .map(([key, value]) => {
      const meaning = value.meaning?.trim();
      if (!meaning) return null;
      const rule = value.rule?.trim() ? formatBucketRuleForDisplay(value.rule.trim(), field) : "";
      if (rule && isSimpleThreshold(rule)) {
        const threshold = simplifyThresholdRule(rule);
        return `${threshold} (${meaning.charAt(0).toLowerCase() + meaning.slice(1)})`;
      }
      return `${formatPolicyEnumDisplayValue(key, field)} (${meaning.charAt(0).toLowerCase() + meaning.slice(1)})`;
    })
    .filter((line): line is string => Boolean(line));

  if (guides.length === 0) return "";
  return `Typical ranges: ${guides.join("; ")}.`;
}

export function buildPolicyRuleFieldExplanation(
  field: TradePolicyCatalogField,
  mergedMetricDefs: Record<string, unknown>
): PolicyRuleFieldExplanation {
  const metricKey = metricKeyForCatalogField(field);
  const catalogDesc = humanizeTechnicalText((field.description ?? "").trim());
  const percentHumanScale = catalogPercentHumanDisplayScale(field);
  const bucketDef = metricKey ? safeBucketMetricDef(metricKey) : null;

  let intro = "";
  if (metricKey && USER_FACING_METRIC_INTROS[metricKey]) {
    intro = USER_FACING_METRIC_INTROS[metricKey];
  } else if (bucketDef?.metric?.trim()) {
    intro = simplifyMetricDescription(bucketDef.metric.trim());
  } else if (metricKey && mergedMetricDefs[metricKey] != null) {
    const metricIntro = metricIntroFromMergedDef(mergedMetricDefs[metricKey]);
    if (metricIntro) {
      intro = simplifyMetricDescription(metricIntro);
    }
  }
  if (!intro && catalogDesc && !looksTechnicalBackendCopy(catalogDesc)) {
    const firstSentence = catalogDesc.split(/(?<=[.!?])\s+/)[0]?.trim();
    intro = firstSentence && firstSentence.length < catalogDesc.length ? firstSentence : catalogDesc;
  }
  if (!intro) {
    intro = `${field.label} is measured on each trade at the moment you enter the position.`;
  }

  const detailParts: string[] = [];
  if (percentHumanScale > 1 && field.value_kind === "number") {
    detailParts.push("Enter whole-number percentages in the value box (for example, 30 for 30%).");
  }

  if (bucketDef?.buckets) {
    if (field.value_kind === "enum") {
      const bucketOptions = buildUserFacingBucketOptions(field, bucketDef.buckets);
      if (bucketOptions) detailParts.push(bucketOptions);
    } else if (field.value_kind === "number") {
      const thresholdGuidance = buildNumericThresholdGuidance(field, bucketDef.buckets);
      if (thresholdGuidance) detailParts.push(thresholdGuidance);
    }
  } else if (field.value_kind === "enum") {
    const choices = formatUserFacingEnumList(field.enum_values ?? [], field);
    if (choices) {
      detailParts.push(`Available choices: ${choices}.`);
    }
  } else if (catalogDesc && catalogDesc !== intro && !looksTechnicalBackendCopy(catalogDesc)) {
    detailParts.push(catalogDesc);
  }

  const summary =
    [intro, ...detailParts].filter(Boolean).join(" ") ||
    "See the metric glossary on your report for more detail.";

  return { summary };
}

export function PolicyRuleFieldExplanationBlock(props: {
  explanation: PolicyRuleFieldExplanation;
  compact?: boolean;
}): React.ReactElement {
  return (
    <div
      className={cn(
        "w-full border-t border-border",
        props.compact ? "mt-2 pt-2" : "mt-3 pt-3",
      )}
    >
      <p className={cn(POLICY_TREE_FIELD_LABEL_CLASS, "mb-1 font-semibold")}>
        What this field means
      </p>
      <p className={cn(POLICY_TREE_SUBTLE_TEXT_CLASS, "text-xs leading-relaxed")}>
        {props.explanation.summary}
      </p>
    </div>
  );
}
